/**
 *
 * Cloud Accounts
 *
 */

import React, { memo, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Card } from 'app/components/DataDisplay/Card';
import {
  addCloudAccountOptions,
  PageHeaderWithIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { AddButton, Select } from 'components/DataEntry';
import {
  FilterComponent,
  useUserPermissionsContext,
} from 'components/FilterView';
import {
  AccountIcon,
  AwsIcon,
  AzureIcon,
  BitbucketIcon,
  GithubIcon,
  GitLabIcon,
  GoogleCloudIcon,
  AzureActiveDirectoryIcon,
  OktaIcon,
  SnowflakeIcon,
  GoogleIcon,
} from 'components/Icons';
import { IMenuItemProps, Menu } from 'components/Overlay';
import { useCloudAccountsFunctions } from 'containers/Setup/CloudAccounts/Components/hooks/useCloudAccountsFunctions';

import { CloudAccountsTable } from './Components/CloudAccountsTable';

export const CloudAccounts = memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userPermission } = useUserPermissionsContext();

  const [refresh, setRefresh] = useState(true);
  //const toggleRefresh = () => setRefresh(!refresh);
  const { getCloudAccounts, cloud, setCloud } = useCloudAccountsFunctions();

  useEffect(() => {
    getCloudAccounts();
    if (refresh) {
      console.log('refreshing');
      setRefresh(false);
    }
  }, [dispatch, refresh, getCloudAccounts]);

  const menuItems: IMenuItemProps[] = [
    {
      label: 'CLOUD',
      group: {
        divider: true,
        list: [
          {
            key: CloudProviders.Aws,
            label: 'Amazon Web Services',
            icon: <AwsIcon />,
            onClick: () => navigate('/setup/accounts/add/aws'),
          },
          {
            key: CloudProviders.Gcp,
            label: 'Google Cloud',
            icon: <GoogleCloudIcon />,
            onClick: () => navigate('/setup/accounts/add/gcp'),
          },
          {
            key: CloudProviders.Azure,
            label: 'Microsoft Azure',
            icon: <AzureIcon />,
            onClick: () => navigate('/setup/accounts/add/azure'),
          },
        ],
      },
    },
    {
      label: 'CODE',
      group: {
        divider: true,
        list: [
          {
            // Previous key: CloudProviders.GitlabIac
            key: CloudProviders.Gitlab,
            label: 'GitLab',
            icon: <GitLabIcon />,
            // Previous route: /setup/accounts/add/gitlab_iac
            onClick: () => navigate('/setup/accounts/add/gitlab'),
          },
          {
            // Previous key: CloudProviders.GithubIac
            key: CloudProviders.Github,
            label: 'GitHub',
            icon: <GithubIcon />,
            // Previous route: /setup/accounts/add/github_iac
            onClick: () => navigate('/setup/accounts/add/github'),
          },
          {
            // Previous key: CloudProviders.BitbucketIac
            key: CloudProviders.Bitbucket,
            label: 'BitBucket',
            icon: <BitbucketIcon />,
            // Previous route: /setup/accounts/add/bitbucket_iac
            onClick: () => navigate('/setup/accounts/add/bitbucket'),
          },
        ],
      },
    },
    {
      label: 'DATA',
      group: {
        divider: true,
        list: [
          {
            key: CloudProviders.Snowflake,
            label: 'Snowflake',
            icon: <SnowflakeIcon />,
            onClick: () => navigate('/setup/accounts/add/snowflake'),
          },
        ],
      },
    },
    {
      label: 'IDENTITY PROVIDER',
      group: {
        list: [
          {
            key: CloudProviders.Okta,
            label: 'Okta (SCIM)',
            icon: <OktaIcon />,
            onClick: () =>
              navigate(`/setup/accounts/add/${CloudProviders.Okta}`),
          },
          {
            key: CloudProviders.AzureAd,
            label: 'Entra ID (SCIM)',
            icon: <AzureActiveDirectoryIcon />,
            onClick: () =>
              navigate(`/setup/accounts/add/${CloudProviders.AzureAd}`),
          },
          {
            key: CloudProviders.GoogleWorkspace,
            label: 'Google Workspace (SCIM)',
            icon: <GoogleIcon />,
            onClick: () =>
              navigate(`/setup/accounts/add/${CloudProviders.GoogleWorkspace}`),
          },
        ],
      },
    },
  ];

  return (
    <Card>
      <Stack h="full" spacing="headerContentSpacing" w="full">
        <HStack w="full" justify="space-between">
          <PageHeaderWithIcon label="Accounts" icon={<AccountIcon />} />
          <Stack>
            <FilterComponent show={userPermission.ariksa.tenancy.create()}>
              <Menu
                menuItems={menuItems}
                buttonLabel={<AddButton label="Add Account" />}
              />
            </FilterComponent>
          </Stack>
        </HStack>
        <BorderedBox>
          <HStack>
            <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>{' '}
            <Box>
              <Select
                placeholder="Cloud"
                showIconInValueContainer
                options={addCloudAccountOptions}
                menuPortalTarget={document.body}
                value={cloud}
                onChange={setCloud}
                isAFilter
                leftMessage="Cloud"
                showUndefinedValueLabel={false}
              />
            </Box>
          </HStack>
        </BorderedBox>
        <Box flex={1}>
          <CloudAccountsTable />
        </Box>
      </Stack>
    </Card>
  );
});
